import set from 'lodash/set';
import createFormField, { isFormField } from './createFormField';
import { flattenFields, getErrorStrs, hasRules, startsWith } from './utils';

function partOf(a: string, b: string) {
    return b.indexOf(a) === 0 && ['.', '['].indexOf(b[a.length]) !== -1;
}

function internalFlattenFields(fields: any) {
    return flattenFields(
        fields,
        (_: any, node: any) => isFormField(node),
        'You must wrap field data with `createFormField`.'
    );
}

class FieldsStore {
  fields: any = {};
  fieldsMeta: any = {};

  constructor(_fields: any) {
      this.fields = internalFlattenFields(_fields);
      this.fieldsMeta = {};
  }

  setFieldsInitialValue = (initialValues: any) => {
      const flattenedInitialValues = this.flattenRegisteredFields(initialValues);
      const { fieldsMeta } = this;
      Object.keys(flattenedInitialValues).forEach((name) => {
          if (fieldsMeta[name]) {
              this.setFieldMeta(name, {
                  ...this.getFieldMeta(name),
                  initialValue: flattenedInitialValues[name],
              });
          }
      });
  };

  updateFields(fields: any) {
      this.fields = internalFlattenFields(fields);
  }

  flattenRegisteredFields(fields: any) {
      const validFieldsName = this.getAllFieldsName();
      return flattenFields(
          fields,
          (path: string) => validFieldsName.indexOf(path) >= 0,
          'You cannot set a form field before rendering a field associated with the value.'
      );
  }

  setFields(fields: any) {
      const { fieldsMeta } = this;
      const nowFields = {
          ...this.fields,
          ...fields,
      };
      const nowValues: any = {};
      Object.keys(fieldsMeta).forEach((f) => {
          nowValues[f] = this.getValueFromFields(f, nowFields);
      });
      Object.keys(nowValues).forEach((f) => {
          const value = nowValues[f];
          const fieldMeta = this.getFieldMeta(f);
          if (fieldMeta && fieldMeta.normalize) {
              const nowValue = fieldMeta.normalize(value, this.getValueFromFields(f, this.fields), nowValues);
              if (nowValue !== value) {
                  nowFields[f] = {
                      ...nowFields[f],
                      value: nowValue,
                  };
              }
          }
      });
      this.fields = nowFields;
  }

  resetFields(ns: any) {
      const { fields } = this;
      const names = ns ? this.getValidFieldsFullName(ns) : this.getAllFieldsName();
      return names.reduce((acc: any, name: string) => {
          const field = fields[name];
          if (field && 'value' in field) {
              acc[name] = {};
          }
          return acc;
      }, {});
  }

  setFieldMeta(name: string, meta: any) {
      this.fieldsMeta[name] = meta;
  }

  setFieldsAsDirty() {
      Object.keys(this.fields).forEach((name) => {
          const field = this.fields[name];
          const fieldMeta = this.fieldsMeta[name];
          if (field && fieldMeta && hasRules(fieldMeta.validate)) {
              this.fields[name] = {
                  ...field,
                  dirty: true,
              };
          }
      });
  }

  getFieldMeta(name: string) {
      this.fieldsMeta[name] = this.fieldsMeta[name] || {};
      return this.fieldsMeta[name];
  }

  getValueFromFields(name: string, fields: any) {
      const field = fields[name];
      if (field && 'value' in field) {
          return field.value;
      }
      const fieldMeta = this.getFieldMeta(name);
      return fieldMeta && fieldMeta.initialValue;
  }

  getValidFieldsName() {
      const { fieldsMeta } = this;
      return fieldsMeta
          ? Object.keys(fieldsMeta).filter((name) => !this.getFieldMeta(name).hidden)
          : [];
  }

  getAllFieldsName() {
      const { fieldsMeta } = this;
      return fieldsMeta ? Object.keys(fieldsMeta) : [];
  }

  getValidFieldsFullName(maybePartialName: string | string[]) {
      const maybePartialNames = Array.isArray(maybePartialName) ? maybePartialName : [maybePartialName];
      return this.getValidFieldsName().filter((fullName) =>
          maybePartialNames.some(
              (partialName) =>
                  fullName === partialName ||
          (startsWith(fullName, partialName) &&
            ['.', '['].indexOf(fullName[partialName.length]) >= 0)
          )
      );
  }

  getFieldValuePropValue(fieldMeta: any) {
      const { name, getValueProps, valuePropName } = fieldMeta;
      const field = this.getField(name);
      const fieldValue = 'value' in field ? field.value : fieldMeta.initialValue;
      if (getValueProps) {
          return getValueProps(fieldValue);
      }
      return { [valuePropName]: fieldValue };
  }

  getField(name: string) {
      return {
          ...this.fields[name],
          name,
      };
  }

  getNotCollectedFields() {
      const fieldsName = this.getValidFieldsName();
      return fieldsName
          .filter((name) => !this.fields[name])
          .map((name) => ({
              name,
              dirty: false,
              value: this.getFieldMeta(name).initialValue,
          }))
          .reduce((acc, field) => set(acc, field.name, createFormField(field)), {});
  }

  getNestedAllFields() {
      return Object.keys(this.fields).reduce(
          (acc, name) => set(acc, name, createFormField(this.fields[name])),
          this.getNotCollectedFields()
      );
  }

  getFieldMember(name: string, member: string) {
      return this.getField(name)[member];
  }

  getAllValues = () => {
      const { fieldsMeta, fields } = this;
      return Object.keys(fieldsMeta).reduce(
          (acc, name) => set(acc, name, this.getValueFromFields(name, fields)),
          {}
      );
  };

  getFieldsValue = (names?: string[]) => {
      return this.getNestedFields(names, this.getFieldValue);
  };

  getFieldValue = (name: string) => {
      const { fields } = this;
      return this.getNestedField(name, (fullName: string) => this.getValueFromFields(fullName, fields));
  };

  getFieldsError = (names?: string[]) => {
      return this.getNestedFields(names, this.getFieldError);
  };

  getFieldError = (name: string) => {
      return this.getNestedField(
          name,
          (fullName: string) => getErrorStrs(this.getFieldMember(fullName, 'errors'))
      );
  };

  isFieldValidating = (name: string) => {
      return this.getFieldMember(name, 'validating');
  };

  isFieldsValidating = (ns?: string[]) => {
      const names = ns || this.getValidFieldsName();
      return names.some((n) => this.isFieldValidating(n));
  };

  isFieldTouched = (name: string) => {
      return this.getFieldMember(name, 'touched');
  };

  isFieldsTouched = (ns?: string[]) => {
      const names = ns || this.getValidFieldsName();
      return names.some((n) => this.isFieldTouched(n));
  };

  getNestedFields(names: any, getter: any) {
      const fields = names || this.getValidFieldsName();
      return fields.reduce((acc: any, f: string) => set(acc, f, getter(f)), {});
  }

  getNestedField(name: string, getter: any) {
      const fullNames = this.getValidFieldsFullName(name);
      if (
          fullNames.length === 0 || // Not registered
      (fullNames.length === 1 && fullNames[0] === name) // Name already is full name.
      ) {
          return getter(name);
      }
      const isArrayValue = fullNames[0][name.length] === '[';
      const suffixNameStartIndex = isArrayValue ? name.length : name.length + 1;
      return fullNames.reduce(
          (acc, fullName) => set(acc, fullName.slice(suffixNameStartIndex), getter(fullName)),
          isArrayValue ? [] : {}
      );
  }

  // @private
  // BG: `a` and `a.b` cannot be use in the same form
  isValidNestedFieldName(name: string) {
      const names = this.getAllFieldsName();
      return names.every((n) => !partOf(n, name) && !partOf(name, n));
  }

  clearField(name: string) {
      delete this.fields[name];
      delete this.fieldsMeta[name];
  }
}

export default function createFieldsStore(fields: any) {
    return new FieldsStore(fields);
}
